import { makeAutoObservable, runInAction, reaction } from 'mobx';
import { onClockInvalidated } from '~/services/time/serverClock';
import {
  SyncService,
  SyncResult,
  SyncServiceConfig,
  ConfigUpdateCallback,
} from '~/services/sync';
import type { SyncZone, AvailableOrientationDto } from '~/api/types/sync';
import { ConnectionMonitorService } from '~/services/connectivity';
import type { ConnectionState } from '~/services/connectivity/types';
import { otaLog } from '~/services/ota/OtaDebugLogger';
import SchedulerStore from './scheduler.store';
import MediaStore from './media.store';
import StreamStore from './stream.store';
import AuthStore from './auth.store';
import { triggerStore } from './trigger.store';
import { readUsableSlotSequence } from '~/services/playback/slotSequenceCache';
import { interactiveElementsStore } from './interactiveElements.store';
import { buildOverlayCacheItems } from '~/services/media/buildOverlayCacheItems';
import { kioskStore } from './kiosk.store';
import { playbackStore } from './playback.store';
import type { PlaylistItem } from '~/services/scheduler/types';

/** Dois impulsos do supervisor em menos que isto: o 2º é rajada e é ignorado. */
const SYNC_CORRECTION_MIN_GAP_MS = 30_000;

// V088 (2026-08-07) — filtro de clima do CONTEÚDO em items vindos do
// slot_sequence Tier 2/3 (server-generated, não passam por scheduler.store).

export type SyncStatus =
  | 'idle'
  | 'syncing'
  | 'success'
  | 'error'
  | 'auth_error';

const RETRY_INTERVAL_SECONDS = 30;
/**
 * 2026-05-24: Debounce de falhas consecutivas antes de promover a
 * status='error' (e por consequência exibir "Sem acesso ao servidor Efflux"
 * no Stream scene). 1 falha (cold-start do Railway, hiccup de rede móvel,
 * throttle de Wi-Fi de loja) NÃO deve mudar mensagem do operador. Default 2
 * — significa que precisa ver 2 syncs consecutivos falhos antes de virar
 * "server_unreachable". Recovery (1 sucesso) é instantâneo, sem debounce.
 *
 * Configurável via VITE_SYNC_FAILURE_DEBOUNCE pra ajustar em campo se
 * necessário (default seguro em 2, dobrar pra 3 em ambientes muito flaky).
 */
const DEFAULT_SYNC_FAILURE_DEBOUNCE = 2;

class SyncStore {
  // Observable state
  status: SyncStatus = 'idle';
  lastSyncTime: string | null = null;
  lastHeartbeatTime: number | null = null;
  libraryHash: string | null = null;
  isOnline: boolean = true;
  /**
   * 2026-06-12 — Probes reais (gstatic + server) feitas pelo
   * ConnectionMonitorService. Substitui o uso de `navigator.onLine`
   * (que mente no Android WebView) pra distinguir "sem internet" de
   * "internet OK mas server inacessível" no NoContent.
   *
   * Estado inicial = true (otimista) pra não mostrar erro antes do
   * primeiro probe terminar (~1-2s após start).
   */
  isInternetReachable: boolean = true;
  isServerReachable: boolean = true;
  connectionState: ConnectionState = 'online';
  error: string | null = null;
  syncCount: number = 0;
  hasContent: boolean = false;
  retryCountdown: number = 0;
  /** Wall-clock target for next retry — UI derives countdown from this. */
  retryTargetAt: number | null = null;
  isRetryingAuth: boolean = false;
  /**
   * Status do player retornado pelo último sync com sucesso. Quando "blocked",
   * o player limpa a playlist e exibe overlay. Quando volta para "active" ou
   * "maintenance", um sync imediato é disparado pra recarregar o conteúdo.
   */
  playerStatus: string | null = null;
  orientacaoId: number | null = null;
  orientationWidth: number | null = null;
  orientationHeight: number | null = null;
  screenOffsetX: number = 0;
  screenOffsetY: number = 0;
  /**
   * Multi-zone layout — zone definitions from the custom orientation.
   * When length > 1, the Stream scene renders N independent ContentViewports
   * (one per zone) with content filtered by zonaId on each PlaylistItem.
   */
  zones: SyncZone[] = [];

  /**
   * Available orientations from the server sync response.
   * Includes global defaults (LANDSCAPE, PORTRAIT) plus tenant-specific
   * custom orientations. Used to render the "Custom" orientation button
   * in Auth and MediaInfo overlays.
   */
  availableOrientations: AvailableOrientationDto[] = [];

  // Service instance
  private syncService: SyncService | null = null;
  /** Momento do último impulso do supervisor aceito (anti-rajada). */
  private lastSyncCorrectionAt = 0;
  private connectionMonitor: ConnectionMonitorService | null = null;
  private retryInterval: ReturnType<typeof setInterval> | null = null;
  private syncStartedAt: number | null = null;
  private syncWatchdogTimeout: ReturnType<typeof setTimeout> | null = null;
  private static readonly SYNC_WATCHDOG_MS = 45_000;
  private configListener: (() => void) | null = null;
  /**
   * 2026-05-24: counter de falhas consecutivas pro debounce. Reset em qualquer
   * sync com sucesso. Vide DEFAULT_SYNC_FAILURE_DEBOUNCE.
   */
  private consecutiveSyncFailures = 0;

  /**
   * Após troca de orientação, força slot 0 + bypass idempotência no
   * próximo setPlaylist (evita continuar campanhas da orientação anterior).
   */
  private resetPlaylistFromStart = false;

  /**
   * 2026-05-31 — WallClockMonitor (midnight bug fix).
   *
   * <p><b>Problema observado em prod:</b> TV Tizen v1.0.85 fica em
   * "Aguardando conteúdo" à meia-noite e não recupera. Tab Android e
   * Browser sobrevivem ao mesmo cenário. Recovery v1.0.42 só dispara quando
   * sync retorna campanhas e playlist=[], mas se o server responde
   * {@code update: false} (libraryHash idêntico) o recovery nem chega no
   * handler. Tizen tem cache mais agressivo e SSE menos confiável, ficando
   * preso até polling natural (5-30min).</p>
   *
   * <p><b>Fix:</b> a cada minuto, comparar {@code Date#getDate()} atual com
   * o snapshot anterior. Quando muda (00:00 local), dispara
   * {@link forceResync} (limpa libraryHash → server reavalia campanhas).
   * Se ainda vier playlist vazia, retry com backoff 30s→60s→120s→240s.</p>
   */
  private wallClockInterval: ReturnType<typeof setInterval> | null = null;
  /** Último valor de Date#getDate() observado pelo wallClock monitor. */
  private lastObservedDate: number | null = null;
  /** Timeouts pendentes do retry backoff da meia-noite. Limpos em reset(). */
  private midnightRetryTimeouts: Array<ReturnType<typeof setTimeout>> = [];
  /**
   * Até quando rejeitamos biblioteca vazia explícita do server após virada
   * de dia (transiente à 00:00 — Tizen SSE sincroniza cedo, tablet/browser
   * segue tocando cache).
   */
  private midnightRecoveryUntil = 0;
  private static readonly MIDNIGHT_RECOVERY_MS = 5 * 60 * 1000;
  /**
   * v1.0.88 — Strikes de sync vazio enquanto player estava tocando.
   * TV Tizen (24/7 + SSE) recebia biblioteca vazia transitória do server
   * (bug endAt 23:59, race SSE) → updateStores([]) apagava campaigns +
   * cache local (163 MB) → "Aguardando conteúdo" enquanto browser/tablet
   * no mesmo horário continuavam (não sincronizaram na janela ruim ou
   * tinham fallback remoto). Após 3 syncs vazios consecutivos, aceita vazio.
   */
  private consecutiveEmptySyncWhilePlaying = 0;
  private static readonly EMPTY_SYNC_GUARD_MAX_STRIKES = 3;
  private lastConfigUpdate: {
    playerId?: string;
    volumeLevel?: number;
    resetVolumeTimeoutSeconds?: number;
    batteryWarningEnabled?: boolean;
    audioAlertEnabled?: boolean;
    audioAlertVolumeLevel?: number;
    audioAlertBeepCount?: number;
    audioAlertIntervalMinutes?: number;
    audioAlertLowBatteryBeepCount?: number;
    audioAlertLowBatteryIntervalMinutes?: number;
    visualAlertEnabled?: boolean;
    visualAlertBlinkCount?: number;
    visualAlertDurationSeconds?: number;
    visualAlertLowBatteryBlinkCount?: number;
    visualAlertLowBatteryDurationSeconds?: number;
    backgroundPortraitUrl?: string;
    backgroundLandscapeUrl?: string;
    kioskModeEnabled?: boolean;
    kioskUnlockPinHashes?: string[];
    // V046 — Brilho do display
    brightnessAuto?: boolean;
    brightnessLevel?: number;
    // V058 — Manter tela sempre ativa (browser)
    keepScreenAwake?: boolean;
    /**
     * De quanto em quanto tempo reportar playback por conta própria, decidido pelo
     * SERVIDOR. `undefined` = servidor não mandou → mantém o legado (15s + troca de
     * slot). `0` = só sob demanda. `N` = a cada N segundos.
     */
    playbackReportIntervalSeconds?: number;
  } | null = null;

  // References to other stores (injected via init)
  private schedulerStore: SchedulerStore | null = null;
  private mediaStore: MediaStore | null = null;
  private streamStore: StreamStore | null = null;
  private authStore: AuthStore | null = null;

  constructor() {
    makeAutoObservable(this);
    // Restore cached available orientations from localStorage
    try {
      const cached = localStorage.getItem('efflux_available_orientations');
      if (cached) {
        this.availableOrientations = JSON.parse(cached);
      }
    } catch {
      // localStorage unavailable or corrupt — ignore
    }
    // Restore cached custom-orientation geometry + multi-zone layout so the
    // player renders zones correctly on an OFFLINE cold boot (before any sync).
    // Without this, `zones` stays empty offline → hasMultipleZones=false → a
    // multi-zone campaign collapses to a single stretched/letterboxed item.
    this.restoreOrientationCache();
    // 🔴 A restauração da PROGRAMAÇÃO **não** vem aqui — vide `init()`. Este é o construtor, e o
    // `streamStore` só é atribuído no `init()`. Chamando daqui, o `applyPlaylistFromPlaybackStore`
    // saía no `if (!this.streamStore) return false` e o atalho não acontecia: medido na TV com
    // `applied=false` no log, e o `playlist` do boot_timing seguiu em ~2350ms em vez de ~1150ms.
  }

  /**
   * Restaura a `slot_sequence` do disco e já aplica a playlist, ANTES de qualquer sync.
   *
   * <p>Não substitui o sync: é atalho pro primeiro frame. O sync roda logo atrás e sobrepõe pelo
   * caminho normal (`setFromSync`). Se não houver cache utilizável, é no-op e o boot segue o
   * comportamento antigo.
   *
   * <p>Os filtros locais de validade/janela continuam valendo: quem monta a playlist a partir da
   * sequência é o mesmo `applyPlaylistFromPlaybackStore` do caminho online, e o `Stream` aplica as
   * 5 camadas AND (conteúdo ∩ item ∩ campanha ∩ grupo ∩ player). Ou seja, conteúdo vencido ou fora
   * de janela NÃO toca por causa deste atalho.
   */
  private restoreSlotSequenceCache(): void {
    try {
      const cached = readUsableSlotSequence();
      if (!cached) return;
      playbackStore.restoreFromCache(
        cached.seq,
        cached.tier,
        cached.canonicalEpoch,
      );
      const applied = this.applyPlaylistFromPlaybackStore();
      const detail = {
        playlistId: cached.seq.playlistId,
        slots: cached.seq.slots.length,
        tier: cached.tier,
        applied,
      };
      if (applied) {
        otaLog.info(
          'SyncStore: programação restaurada do disco (boot rápido)',
          detail,
        );
      } else {
        // WARN, não info: cache válido + `applied=false` significa que o atalho existe mas não está
        // sendo consumido, e o boot volta a pagar o round-trip de sync SEM nenhum sinal de erro.
        // Foi assim que passou desapercebido na 1.0.312 (o restore rodava no construtor, antes de o
        // `streamStore` existir) e só apareceu porque fui ler o console na TV.
        otaLog.warn(
          'SyncStore: cache de programação VÁLIDO mas não aplicado — boot vai esperar o sync',
          detail,
        );
      }
    } catch (err) {
      // Best-effort: qualquer falha aqui só devolve o boot ao caminho antigo (esperar o sync).
      otaLog.warn('SyncStore: falha ao restaurar programação do disco', {
        error: String(err),
      });
    }
  }

  /**
   * Boot offline — empurra a playlist do scheduler (Tier 4 / legado) pro stream UMA vez.
   *
   * <p>O `restoreSlotSequenceCache` acima só cobre Tier 2/3 (slot_sequence). O `schedulerStore`
   * restaura as campanhas do disco no construtor (`efflux_cached_campaigns`) e computa a
   * `activePlaylist`, mas o `evaluateSchedule` que rodou lá disparou os listeners ANTES de o
   * `init` assinar `onScheduleChange` — e, sem mudança de campanha/vacuidade, o tick de 15s não
   * re-notifica (ex.: 1 campanha estável). Sem este empurrão, o boot offline de um player Tier 4
   * carregava as campanhas mas NUNCA as entregava ao stream → "Sem conexão"/NoContent com a mídia
   * toda em disco (mandato offline-first independente de tier, Renato 2026-11-04).
   *
   * <p>Só age fora do modo playlist (Tier 2/3 já coberto) e quando há algo restaurado pra tocar.
   * Best-effort: o sync, quando vier, sobrepõe pelo caminho normal (`applyStreamPlaylist`).
   */
  private restoreSchedulerPlaylistFromCache(): void {
    try {
      if (playbackStore.isPlaylistMode) return; // Tier 2/3 já coberto por restoreSlotSequenceCache
      if (!this.streamStore || !this.schedulerStore) return;
      const items = this.schedulerStore.getActivePlaylist();
      if (items.length === 0) return; // nada em cache pra tocar — boot segue esperando o sync
      this.applyStreamPlaylist('boot-cache-restore');
      otaLog.info(
        'SyncStore: playlist do scheduler (Tier 4/legado) restaurada do disco (boot offline)',
        { items: items.length },
      );
    } catch (err) {
      otaLog.warn(
        'SyncStore: falha ao restaurar playlist do scheduler no boot',
        { error: String(err) },
      );
    }
  }

  /**
   * Persists the custom-orientation geometry + zone layout to localStorage.
   * `zones` (and the orientation dims) only arrive via online sync, but the
   * multi-zone renderer needs them on an offline cold boot too — the cached
   * campaigns carry each item's `zonaId`, but NOT the zone rectangles. Called
   * on every successful sync to keep the offline copy fresh.
   */
  private persistOrientationCache(): void {
    try {
      localStorage.setItem(
        'efflux_cached_orientation',
        JSON.stringify({
          orientacaoId: this.orientacaoId,
          orientationWidth: this.orientationWidth,
          orientationHeight: this.orientationHeight,
          screenOffsetX: this.screenOffsetX,
          screenOffsetY: this.screenOffsetY,
          zones: this.zones,
        }),
      );
    } catch {
      // localStorage unavailable — ignore (offline falls back to single viewport)
    }
  }

  /** Restores the cached orientation geometry + zones (see persistOrientationCache). */
  private restoreOrientationCache(): void {
    try {
      const cached = localStorage.getItem('efflux_cached_orientation');
      if (!cached) return;
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object') {
        this.orientacaoId = parsed.orientacaoId ?? null;
        this.orientationWidth = parsed.orientationWidth ?? null;
        this.orientationHeight = parsed.orientationHeight ?? null;
        this.screenOffsetX = parsed.screenOffsetX ?? 0;
        this.screenOffsetY = parsed.screenOffsetY ?? 0;
        this.zones = Array.isArray(parsed.zones) ? parsed.zones : [];
      }
    } catch {
      // localStorage unavailable or corrupt — ignore
    }
  }

  // ===========================================================================
  // RETRY COUNTDOWN
  // ===========================================================================

  /**
   * Starts countdown for retry when no content is available.
   *
   * 2026-05-31 — Timestamp-based em vez de decremento por tick.
   * Tizen WebView congela o event loop por minutos (cache I/O, OOM,
   * html2canvas do admin); setInterval de 1s só dispara 4× em 8min.
   * Com target wall-clock, ao retomar o tick calcula o restante real
   * e dispara sync imediatamente se já passou do prazo.
   */
  startRetryCountdown(): void {
    this.stopRetryCountdown();

    const targetAt = Date.now() + RETRY_INTERVAL_SECONDS * 1000;
    runInAction(() => {
      this.retryTargetAt = targetAt;
      this.retryCountdown = RETRY_INTERVAL_SECONDS;
    });

    this.retryInterval = setInterval(() => this.tickRetryCountdown(), 500);
  }

  private tickRetryCountdown(): void {
    if (this.retryTargetAt === null) return;

    const remaining = Math.max(
      0,
      Math.ceil((this.retryTargetAt - Date.now()) / 1000),
    );

    runInAction(() => {
      if (remaining !== this.retryCountdown) {
        this.retryCountdown = remaining;
      }

      if (remaining <= 0) {
        this.stopRetryCountdown();
        this.sync().catch((err) =>
          console.warn('[SyncStore] Retry sync failed:', err),
        );
      }
    });
  }

  /**
   * Inicia o WallClockMonitor — dispara forceResync na virada de dia.
   *
   * <p>Idempotente (chamar 2x não duplica o interval). Verifica a cada minuto
   * porque a granularidade do bug é "cruzou meia-noite". Não usa setTimeout
   * agendado pra próxima meia-noite porque, em ambientes onde o relógio
   * pode mudar (TV ligando após queda de luz, NTP sync), o setTimeout
   * absoluto fica errado — polling de 1min é robusto.</p>
   *
   * <p>Quando {@code Date#getDate()} mudou desde o último tick:
   * <ol>
   *   <li>Dispara {@link forceResync} imediato (limpa libraryHash).</li>
   *   <li>Agenda retries com backoff: 30s, 60s, 120s, 240s.</li>
   *   <li>Cada retry verifica se já há conteúdo ativo
   *       ({@code streamStore.hasNext}). Se sim, cancela os pendentes.</li>
   * </ol></p>
   */
  private startWallClockMonitor(): void {
    if (this.wallClockInterval) return; // idempotente

    this.lastObservedDate = new Date().getDate();
    console.log(
      '[SyncStore] WallClockMonitor iniciado — observando virada de dia (current:',
      this.lastObservedDate,
      ')',
    );

    this.wallClockInterval = setInterval(() => {
      const currentDate = new Date().getDate();
      if (
        this.lastObservedDate !== null &&
        currentDate !== this.lastObservedDate
      ) {
        const prev = this.lastObservedDate;
        this.lastObservedDate = currentDate;
        console.warn(
          `[SyncStore] 🌙 Virada de dia detectada (${prev} → ${currentDate}). Disparando forceResync + retry backoff.`,
        );
        this.triggerMidnightResync();
      } else {
        this.lastObservedDate = currentDate;
      }
    }, 60000); // 1 minuto
  }

  /**
   * Para o WallClockMonitor e limpa retries pendentes do backoff.
   * Chamado em {@link reset}.
   */
  private stopWallClockMonitor(): void {
    if (this.wallClockInterval) {
      clearInterval(this.wallClockInterval);
      this.wallClockInterval = null;
    }
    this.midnightRetryTimeouts.forEach((t) => clearTimeout(t));
    this.midnightRetryTimeouts = [];
    this.lastObservedDate = null;
  }

  /**
   * Dispara forceResync imediato + retry backoff pra cobrir cenário de
   * server retornando playlist vazia logo após meia-noite (ex: campanha A
   * expirou, campanha B startTime > 00:00, então server retorna campaigns
   * mas getActivePlaylist=[]).
   *
   * <p>Backoff: 30s, 60s, 120s, 240s. Cada tentativa cancela as próximas
   * se {@code streamStore.hasNext} = true (já recuperou).</p>
   *
   * <p><b>v1.0.165 (Renato + Claude, 2026-06-09)</b>: REMOVIDO o
   * {@code this.hasContent = false} síncrono que existia aqui.
   *
   * Bug observado em prod: tablet Tab A7 (Android v1.0.164) E TV Tizen
   * ficavam com tela preta TODA noite à 00:00, mesmo com campanhas
   * válidas (endDate=2099-12-31, scheduler returning 14 items).
   * Logs da TV às 00:17: sync OK, activePlaylist=14, ProtectedRoute
   * Authorized — mas tela preta. Reload do app resolvia (até a
   * próxima meia-noite).
   *
   * Causa raiz: o {@code hasContent = false} aqui era SÍNCRONO,
   * disparado imediatamente na virada de dia. O React re-renderizava
   * {@code <NoContent>} (tela preta) antes do {@code forceResync}
   * abaixo terminar (~500ms depois). Quando o sync voltava com as
   * MESMAS campanhas (caso comum: endDate=2099), o flag não voltava
   * automaticamente pra true porque {@code library_updated} não
   * disparava — tela ficava preta até o usuário recarregar.
   *
   * Trade-off: removendo o zeramento síncrono, em cenários REAIS de
   * transição A→B com gap (campanha A expirou às 23:59, B começa
   * 00:00 com startTime > 00:00 — raro), o player tenta tocar o
   * último vídeo de A por ~500ms até o forceResync responder.
   * Visualmente: 500ms de continuação >>> 7+ min de tela preta
   * sustentada. Codepaths regulares (updateStores etc.) cuidam de
   * setar hasContent=false QUANDO o server confirma biblioteca vazia.</p>
   */
  private triggerMidnightResync(): void {
    if (!this.syncService) return;

    this.midnightRecoveryUntil = Date.now() + SyncStore.MIDNIGHT_RECOVERY_MS;
    // v1.0.165: NÃO zera hasContent aqui (vide JSDoc acima). Deixa
    // o forceResync decidir baseado na resposta do server. Codepaths
    // regulares (updateStores, EMPTY_SYNC_GUARD) lidam com hasContent.
    this.startRetryCountdown();

    // Disparo imediato
    this.syncService
      .forceResync()
      .catch((err) =>
        console.warn(
          '[SyncStore] Midnight forceResync (imediato) falhou:',
          err,
        ),
      );

    // Retry backoff
    const delays = [30000, 60000, 120000, 240000];
    delays.forEach((delay, idx) => {
      const timeout = setTimeout(() => {
        if (this.streamStore?.hasNext) {
          console.log(
            `[SyncStore] Midnight retry #${idx + 1} cancelado — streamStore.hasNext já é true.`,
          );
          return;
        }
        if (!this.syncService) return;
        console.log(
          `[SyncStore] 🔄 Midnight retry #${idx + 1} (após ${delay / 1000}s)`,
        );
        this.syncService
          .forceResync()
          .catch((err) =>
            console.warn(`[SyncStore] Midnight retry #${idx + 1} falhou:`, err),
          );
      }, delay);
      this.midnightRetryTimeouts.push(timeout);
    });
  }

  /**
   * Scheduler/stream sem itens tocáveis — alinha hasContent, retry e pede
   * payload fresco (hash pode estar stale enquanto tablet ainda toca cache).
   */
  private reconcileEmptyPlaybackState(reason: string): void {
    if (!this.schedulerStore) return;
    if (this.schedulerStore.getActivePlaylist().length > 0) return;

    console.warn(
      `[SyncStore] 🌙 Playback vazio (${reason}) — hasContent=false + retry + forceResync`,
    );
    runInAction(() => {
      this.hasContent = false;
    });
    this.startRetryCountdown();
    if (this.syncService) {
      this.syncService
        .forceResync()
        .catch((err) =>
          console.warn(
            '[SyncStore] reconcileEmptyPlaybackState forceResync falhou:',
            err,
          ),
        );
    }
  }

  /**
   * Stops the retry countdown.
   */
  stopRetryCountdown(): void {
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
      this.retryInterval = null;
    }
    runInAction(() => {
      this.retryCountdown = 0;
      this.retryTargetAt = null;
    });
  }

  private clearSyncWatchdog(): void {
    if (this.syncWatchdogTimeout) {
      clearTimeout(this.syncWatchdogTimeout);
      this.syncWatchdogTimeout = null;
    }
  }

  /**
   * Se sync() ficar pendente >45s (axios hang no Tizen, rede zombie),
   * força recovery e re-agenda retry pra não ficar eternamente em
   * "Sincronizando..." com última sync congelada.
   */
  private armSyncWatchdog(): void {
    this.clearSyncWatchdog();
    this.syncStartedAt = Date.now();
    this.syncWatchdogTimeout = setTimeout(() => {
      if (this.status !== 'syncing' || this.syncStartedAt === null) return;

      const elapsed = Date.now() - this.syncStartedAt;
      if (elapsed < SyncStore.SYNC_WATCHDOG_MS) return;

      console.warn(
        `[SyncStore] Sync watchdog — stuck ${Math.round(elapsed / 1000)}s, forcing recovery`,
      );
      runInAction(() => {
        this.status = 'error';
        this.error = 'Sync timeout — retrying';
      });
      if (!this.hasContent) {
        this.startRetryCountdown();
      }
    }, SyncStore.SYNC_WATCHDOG_MS);
  }

  // ===========================================================================
  // COMPUTED
  // ===========================================================================

  get isInitialized(): boolean {
    return this.syncService !== null;
  }

  get isSyncing(): boolean {
    return this.status === 'syncing';
  }

  get hasError(): boolean {
    return this.status === 'error' && this.error !== null;
  }

  get lastSyncTimeFormatted(): string {
    if (!this.lastSyncTime) return 'Never';
    try {
      return new Date(this.lastSyncTime).toLocaleTimeString();
    } catch {
      return this.lastSyncTime;
    }
  }

  /** True quando o servidor reportou o player como "blocked" no último sync. */
  get isPlayerBlocked(): boolean {
    return this.playerStatus === 'blocked';
  }

  get hasCustomOrientation(): boolean {
    return (
      this.orientationWidth != null &&
      this.orientationHeight != null &&
      this.orientationWidth > 0 &&
      this.orientationHeight > 0
    );
  }

  /**
   * True when the custom orientation defines multiple zones.
   * When true, the Stream scene renders N independent ContentViewports
   * instead of a single viewport.
   */
  get hasMultipleZones(): boolean {
    return this.zones.length > 1;
  }

  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================

  /**
   * Initializes the sync store with configuration and store references.
   */
  init(
    config: SyncServiceConfig,
    stores: {
      schedulerStore: SchedulerStore;
      mediaStore: MediaStore;
      streamStore: StreamStore;
      authStore: AuthStore;
    },
  ): void {
    if (this.syncService) {
      console.warn('[SyncStore] Already initialized');
      return;
    }

    this.schedulerStore = stores.schedulerStore;
    this.mediaStore = stores.mediaStore;
    this.streamStore = stores.streamStore;
    this.authStore = stores.authStore;

    // FIM DE GATILHO EM GRUPO EXCLUSIVO — o gatilho devolve o player ao loop pelo MESMO
    // caminho do impulso do supervisor (reentrada pelo relógio), em vez de restaurar a
    // posição salva no disparo. Injetado aqui (e não importado lá) porque este arquivo
    // já importa o `triggerStore` — mesmo padrão do `setStreamStore`/`setSchedulerStore`.
    triggerStore.setRealignHandler((source) =>
      this.realignToCanonicalClock(source),
    );
    // Mesma reentrada ao soltar o freio de cascata (StreamStore.handleMediaFailure): enquanto
    // o player segurava a tela, o relógio do grupo continuou andando.
    this.streamStore?.setRealignHandler((source) =>
      this.realignToCanonicalClock(source),
    );
    // O streamStore TAMBÉM vai aqui. Antes só era injetado quando o sync trazia
    // definições de gatilho (vide handleSyncResult) — um player SEM definição
    // pré-cacheada (ex.: grupo exclusivo, que não as recebe) que recebesse um
    // `trigger_fired` por SSE marcava `playing` sem trocar a playlist: o gatilho
    // disparava no vazio. Medido na bancada em 2026-10-15 (tablet em grupo
    // exclusivo, is_trigger nunca virou true).
    triggerStore.setStreamStore(this.streamStore);

    // PROGRAMAÇÃO DO DISCO — aqui, e não no construtor: só a partir desta linha o `streamStore`
    // existe, e é ele que o `applyPlaylistFromPlaybackStore` precisa. Antes disto o atalho era
    // recusado silenciosamente (`applied=false`, medido na TV).
    //
    // Vem ANTES do `syncService` ser criado de propósito: o objetivo é o 1º frame não esperar a
    // rede. Medido na QB50B: `react` em ~1000ms mas playlist conhecida só em ~2350ms — 1,3s de tela
    // apagada esperando um round-trip, num aparelho com a mídia toda em disco. E como o refresh
    // periódico recarrega o app, esse custo era pago a cada reciclagem.
    this.restoreSlotSequenceCache();
    // Tier 4 / legado: o atalho acima só cobre Tier 2/3 (slot_sequence). O schedulerStore já
    // restaurou as campanhas do disco no construtor, mas o evaluateSchedule de lá notificou ANTES
    // de este init assinar onScheduleChange — e o tick de 15s não re-notifica se nada muda. Sem
    // este empurrão, Tier 4 offline carrega as campanhas e nunca as entrega ao stream (NoContent
    // com a mídia em disco). Espelha o applyPlaylistFromPlaybackStore do Tier 2/3.
    this.restoreSchedulerPlaylistFromCache();

    // Initialize MediaStore to load cached metadata from previous sessions
    this.initMediaStore();

    this.syncService = new SyncService(config);

    // O relógio do device saltou (NTP acertou) ou o aparelho dormiu: o offset do servidor
    // virou palpite. Um sync agora traz `sync_timestamp` com RTT medido e substitui o
    // palpite inteiro — sem isto o videowall tocaria fora de fase até a próxima batida
    // ou, em modo polling, até o próximo sync de 30 min.
    onClockInvalidated(() => {
      this.sync().catch(() => undefined);
    });

    // 2026-06-12 — ConnectionMonitorService faz probes paralelas:
    // (a) gstatic.com/generate_204 → detecta internet de verdade
    // (b) {contentApiUrl}/actuator/health → detecta server alcançável
    //
    // Permite distinguir "sem internet" (caso A falha) de "internet OK
    // mas server inacessível" (caso B falha mas A passa) — antes
    // dependíamos de navigator.onLine que mente no Android WebView.
    // Resultado vai pros 3 observables (isInternetReachable,
    // isServerReachable, connectionState) usados pelo Stream pra escolher
    // a msg correta no NoContent.
    this.connectionMonitor = new ConnectionMonitorService({
      serverUrl: config.contentApiUrl,
      // Fase 1 (descoberta em camadas): o ping de liveness de 30 s passa a bater
      // no content-version — um request faz prova de vida E frescor. Serve de
      // rede de segurança (pega mudança perdida mesmo com o SSE ativo) e é o
      // canal barato do excedente que não cabe no SSE. Zero conexão nova.
      contentVersionPath: '/api/v1/content-version',
      tenantId: config.tenantId,
    });

    // Gate de conectividade do sync: `navigator.onLine` **OU** a probe real ao
    // servidor. O OR é deliberado e é o que torna esta mudança segura.
    //
    // Substituir um pelo outro seria regressão: se a probe desse falso-negativo
    // (endpoint filtrado numa loja, por exemplo), o sync pararia num aparelho onde
    // hoje funciona. Com OR, o conjunto de situações em que sincronizamos só
    // AUMENTA — nunca diminui.
    //
    //   Wi-Fi normal, servidor OK   → onLine=true                  → sincroniza (igual a hoje)
    //   Wi-Fi normal, servidor fora → onLine=true                  → tenta e falha (igual a hoje)
    //   Túnel (USB), sem interface  → onLine=FALSE, server=true    → sincroniza  ← o que muda
    //   Sem rede nenhuma            → ambos false                  → não tenta (igual a hoje)
    //
    // `isServerReachable` (e não `isInternetReachable`) porque o que importa para
    // sincronizar é alcançar o Efflux; começa `true` (otimista) e só cai após
    // falhas consecutivas, então não segura o primeiro sync do boot.
    this.syncService?.setOnlineProvider(() => {
      const navegadorDizOnline =
        typeof navigator !== 'undefined' ? navigator.onLine : true;
      const servidorAlcancavel =
        this.connectionMonitor?.isServerReachable ?? false;
      return navegadorDizOnline || servidorAlcancavel;
    });
    this.connectionMonitor.onStateChange((state) => {
      runInAction(() => {
        this.connectionState = state;
        this.isInternetReachable = this.connectionMonitor!.isInternetReachable;
        this.isServerReachable = this.connectionMonitor!.isServerReachable;
      });
    });
    // Ao detectar frescor no ping, dispara o MESMO caminho de um content_changed
    // (zera hash + respeita o sync_after_seconds do servidor). Idempotente com o
    // SSE/poller: se dois caminhos virem a mesma mudança, o 2º sync sai barato.
    this.connectionMonitor.onNeedsSync((syncAfterSeconds) => {
      console.log(
        '[SyncStore] 🩺 ping de liveness detectou frescor → requestContentSync',
      );
      this.syncService?.requestContentSync(syncAfterSeconds);
    });
    this.connectionMonitor.start();

    // Listen for sync results
    this.syncService.onSyncComplete(this.handleSyncResult);

    // Listen for connectivity changes
    this.syncService.onConnectivityChange((online) => {
      runInAction(() => {
        this.isOnline = online;
      });
    });

    // Listen for heartbeats
    this.syncService.onHeartbeat((timestamp) => {
      runInAction(() => {
        this.lastHeartbeatTime = timestamp;
      });
    });

    // When the scheduler's time-based filtering changes what's playable, update the stream immediately.
    // This handles the case where an exclusive playlist's time window expires between syncs.
    this.schedulerStore.onScheduleChange((campaign) => {
      if (!this.streamStore || triggerStore.isPlaying) return;
      this.applyStreamPlaylist('schedule-change', campaign?.id);
      if (
        !playbackStore.isPlaylistMode &&
        this.schedulerStore!.getActivePlaylist().length === 0
      ) {
        this.reconcileEmptyPlaybackState('schedule-change');
      }
    });

    // Listen for trigger SSE events
    this.syncService.onTriggerFired((payload) => {
      console.log('[SyncStore] 🎯 SSE trigger_fired → TriggerStore:', payload);
      triggerStore.handleTriggerFired(payload);
    });
    this.syncService.onTriggerDeactivated((payload) => {
      console.log(
        '[SyncStore] ⏹️ SSE trigger_deactivated → TriggerStore:',
        payload,
      );
      triggerStore.handleTriggerDeactivated(payload);
    });

    // IMPULSO DO SUPERVISOR DE SINCRONISMO (server). O relógio já foi acertado no
    // SyncService; aqui realinhamos a POSIÇÃO. Época diferente dos peers = a sequência
    // deste player está velha → sync completo. Só desvio = reentra pelo relógio na
    // sequência que já tem (mesmo caminho do forceFromStart, sem rede).
    // Sem o impulso nada muda: época + relógio mantêm o player em fase offline.
    this.syncService.onSyncCorrection((payload) => {
      const now = Date.now();
      if (now - this.lastSyncCorrectionAt < SYNC_CORRECTION_MIN_GAP_MS) {
        otaLog.info('sync_correction ignorado (rajada)', {
          reason: payload.reason,
        });
        return;
      }
      this.lastSyncCorrectionAt = now;
      const epochMismatch =
        payload.reason === 'epoch_mismatch' ||
        (typeof payload.canonical_epoch === 'string' &&
          playbackStore.canonicalEpoch != null &&
          payload.canonical_epoch !== playbackStore.canonicalEpoch);
      otaLog.info('sync_correction recebido', {
        reason: payload.reason,
        deviationSeconds: payload.deviation_seconds,
        epochMismatch,
      });
      if (epochMismatch || playbackStore.canonicalEpoch == null) {
        this.forceResync().catch(() => undefined);
        return;
      }
      this.realignToCanonicalClock('sync-correction');
    });

    // Listen for remote restart command (PLAYER_RESTART_PLAN)
    this.syncService.onRestartCommand((payload) => {
      console.log(
        '[SyncStore] 🔄 SSE restart_command → RestartService:',
        payload,
      );
      // Lazy import pra evitar dependência circular e adiar load do platform service
      import('../services/restart/RestartService').then(
        ({ default: RestartService }) => {
          const platformService = (
            window as unknown as {
              __effluxPlatform?: import('~/services/platform/types').IPlatformService;
            }
          ).__effluxPlatform;
          RestartService.handle(payload, platformService).catch((err) =>
            console.warn('[SyncStore] RestartService failed:', err),
          );
        },
      );
    });

    // Listen for orientation change pushed by the admin (SSE
    // "player_orientation_changed"). Aplica override no authStore +
    // forceResync — o conteúdo da nova orientação aparece imediatamente.
    // Sem isso o admin troca o dropdown mas o player só descobre no
    // próximo poll (5-30 min).
    this.syncService.onOrientationChangeFromAdmin((orientation) => {
      console.log(
        '[SyncStore] 🔁 SSE player_orientation_changed → applying override:',
        orientation,
      );
      if (this.authStore) {
        this.authStore.setSelectedOrientation(orientation);
      }
      // Re-sync pra trocar conteúdo da orientação correta. Sem isso o
      // tablet ficaria com o playlist antigo até o próximo /sync.
      this.forceResync({ resetPlaylistFromStart: true }).catch((err) =>
        console.warn(
          '[SyncStore] forceResync após orientation_changed falhou:',
          err,
        ),
      );
    });

    // Set initial online status
    runInAction(() => {
      this.isOnline = this.syncService!.isOnline();
    });

    // React to authentication state changes from AuthStore
    // When device becomes authenticated (approved), trigger sync
    if (this.authStore) {
      reaction(
        () => ({
          isAuthenticated: this.authStore?.isAuthenticated,
          registrationStatus: this.authStore?.registrationStatus,
        }),
        ({ isAuthenticated, registrationStatus }) => {
          // Only trigger sync if authenticated AND approved (not pending)
          if (
            isAuthenticated &&
            registrationStatus === 'approved' &&
            this.authStore?.deviceId
          ) {
            // 2026-06-12 (Renato): cache-first no boot offline. Antes de
            // disparar `sync()` (que pode levar até 30s pra falhar sem
            // internet), tenta inicializar o stream a partir do cache do
            // scheduler. Isso garante que se o scheduler já tem campanhas
            // carregadas do localStorage (via constructor.loadFromCache),
            // o Stream começa a tocar IMEDIATAMENTE — antes da network
            // timing-out.
            //
            // Comportamento online (caminho feliz): cache mostra brevemente
            // (~100ms) até sync retornar com versão fresh. Comportamento
            // offline: cache é a única fonte por enquanto, e quando sync
            // eventualmente falhar (30s depois), o handleSyncResult não
            // limpa hasContent (mantém o cache no ar).
            if (!this.hasContent && this.schedulerStore?.hasContent) {
              console.log(
                '[SyncStore] 🚀 Cache-first boot: inicializando do cache antes do sync',
              );
              this.initializeFromCache();
            }

            console.log(
              '[SyncStore] Device authenticated and approved, triggering sync',
            );
            // Trigger immediate sync now that device is approved
            this.sync();
          } else {
            console.log('[SyncStore] Not triggering sync:', {
              isAuthenticated,
              registrationStatus,
              reason: !isAuthenticated
                ? 'Not authenticated'
                : registrationStatus !== 'approved'
                  ? `Status: ${registrationStatus}`
                  : 'No deviceId',
            });
          }
        },
      );

      // If device is already authenticated when sync is initialized,
      // trigger sync immediately (reaction only fires on changes, not initial state)
      // BUT only if status is 'approved', not 'pending_approval'
      if (
        this.authStore.isAuthenticated &&
        this.authStore.registrationStatus === 'approved' &&
        this.authStore.deviceId
      ) {
        console.log(
          '[SyncStore] Device already authenticated and approved at init, triggering immediate sync',
        );
        // Use setTimeout to ensure sync service is fully ready
        setTimeout(() => {
          this.sync();
        }, 100);
      } else {
        console.log('[SyncStore] Not triggering sync at init:', {
          isAuthenticated: this.authStore.isAuthenticated,
          registrationStatus: this.authStore.registrationStatus,
          hasDeviceId: !!this.authStore.deviceId,
        });
      }

      // React to device ID changes from AuthStore
      // This ensures sync uses the correct hardware ID when it's detected
      reaction(
        () => this.authStore?.deviceId,
        (newDeviceId) => {
          if (newDeviceId && this.syncService) {
            console.log(
              '[SyncStore] AuthStore deviceId changed, updating SyncService',
            );
            this.syncService.setDeviceId(newDeviceId);
          }
        },
      );
    }

    // Subscribe to config updates permanently
    if (this.syncService) {
      this.configListener = this.syncService.onConfigUpdate((config) => {
        console.log('[SyncStore] 🔄 Internal config update received:', config);
        runInAction(() => {
          this.lastConfigUpdate = config;
          // Só atualiza se vier valor explícito true/false (vide doc abaixo).
          if (
            config.kioskModeEnabled === true ||
            config.kioskModeEnabled === false
          ) {
            kioskStore.setServerEnabled(config.kioskModeEnabled);
          }
          if (config.kioskUnlockPinHashes !== undefined) {
            kioskStore.setUnlockPinHashes(config.kioskUnlockPinHashes);
          }
        });
        // Tenta drenar fila offline de auditoria após receber config
        // (geralmente significa que estamos online).
        this.drainKioskAuditQueue().catch((err) =>
          console.warn('[SyncStore] Drain audit queue failed:', err),
        );
      });
    }

    // Fetch initial config if authenticated
    if (this.authStore?.isAuthenticated && this.authStore?.deviceId) {
      console.log('[SyncStore] Fetching initial config...');
      this.fetchConfig().catch((err) =>
        console.warn('[SyncStore] Failed to fetch initial config', err),
      );
    }

    console.log('[SyncStore] Initialized with config:', {
      deviceId: config.deviceId,
      isAuthenticated: this.authStore?.isAuthenticated,
    });
  }

  /**
   * @deprecated API key authentication is deprecated. Authentication uses device ID now.
   */
  setApiKey(_apiKey: string): void {
    console.warn(
      '[SyncStore] setApiKey is deprecated - authentication uses device ID',
    );
  }

  /**
   * Activates demo mode: marks sync as successful without any backend calls.
   * Called by DemoModeService when VITE_DEMO_MODE=true.
   */
  setDemoMode(): void {
    runInAction(() => {
      this.status = 'success';
      this.hasContent = true;
      this.isOnline = false; // demo não tem rede real
      this.syncCount = 1;
    });
  }

  // ===========================================================================
  // ACTIONS
  // ===========================================================================

  /**
   * Sprint 2 CP9 — decide a fonte da playlist enviada ao StreamStore.
   *
   * <p>Em Tier 2/3 ({@code playbackStore.isPlaylistMode === true}), a
   * fonte autoritativa é o {@code slot_sequence} que veio do backend —
   * já é uma sequência ordenada e filtrada pra esta janela horária.
   * O scheduler legacy é ignorado nesse modo.</p>
   *
   * <p>Em Tier 1/4 (legado), nada muda: scheduler segue como única fonte.</p>
   *
   * <p>Idempotência preservada: {@link StreamStore.setPlaylist} já tem
   * lógica que detecta playlist idêntica e preserva {@code currentIndex}.
   * Repetir essa chamada em syncs sem mudança não causa re-start do loop.</p>
   *
   * @returns true se aplicou a partir da playbackStore (Tier 2/3),
   *          false se delegou pro fluxo legado (caller faz fallback).
   */
  private applyPlaylistFromPlaybackStore(forceFromStart = false): boolean {
    if (!this.streamStore) return false;
    if (!playbackStore.isPlaylistMode) return false;
    if (!playbackStore.currentSlotSequence) {
      otaLog.warn('applyPlaylistFromPlaybackStore: sem slot_sequence', {
        tier: playbackStore.currentTier,
      });
      return false;
    }
    if (triggerStore.isPlaying) return false; // Trigger tem prioridade

    // V088 (2026-08-08) — slot_sequence é gerada no server e agora carrega
    // TODOS os campos do CONTEÚDO (weather + validFrom/until + daysOfWeek +
    // startAt/endAt + scheduleMode + weeklySchedule) — paridade com Tier 4.
    // Delega ao filtro canônico do scheduler pras 6 camadas AND
    // (Conteúdo ∩ PlaylistItem ∩ Campanha ∩ Grupo ∩ Player ∩ Playlist)
    // + weather do conteúdo. Antes o filtro aqui só cobria weather e o
    // item piscava por 1 frame antes do scheduler removê-lo. Regra vazia →
    // passa; sem dado de clima + regra setada → conservador (não toca).
    // Vide regras-temporais-player.md R0/R2/R4.
    const rawItems = playbackStore.toPlaylistItems();
    const items = this.schedulerStore
      ? this.schedulerStore.filterPlaylistItemsBySchedule(rawItems)
      : rawItems;
    const filteredOut = rawItems.length - items.length;
    if (filteredOut > 0) {
      otaLog.info('applyPlaylistFromPlaybackStore: schedule filtered', {
        tier: playbackStore.currentTier,
        total: rawItems.length,
        filtered: filteredOut,
      });
    }
    if (items.length === 0) {
      otaLog.warn('applyPlaylistFromPlaybackStore: slots vazios', {
        tier: playbackStore.currentTier,
        playlistId: playbackStore.currentSlotSequence.playlistId,
      });
      return false;
    }

    // Layout multi-zona: slot_sequence é sempre autoritativa em Tier 2/3.
    // Stream/index.tsx renderiza zonas quando currentMedia.zonaId != null.

    const seq = playbackStore.currentSlotSequence;
    const campaignId = `playlist-${seq.playlistId}`;
    const anchored = playbackStore.canonicalEpoch != null;
    // Com época canônica, o índice de entrada é SEMPRE o do relógio: `startIndex` 0 faz o
    // `resolveInitialIndex` ancorar. Passar o `currentSlotIndex` aqui era o furo: ele só avança
    // em `playNext` (não nos hops do pulso), então um sync que mudava duração/URL sem mudar os
    // contentIds entrava num índice velho e o pulso corrigia 2s depois — pulo visível.
    const startIndex =
      forceFromStart || anchored ? 0 : playbackStore.currentSlotIndex;

    this.streamStore.setPlaylist(
      items,
      campaignId,
      false,
      startIndex,
      forceFromStart,
    );
    otaLog.info('Playlist source: slot_sequence', {
      tier: playbackStore.currentTier,
      slots: items.length,
      playlistId: seq.playlistId,
      hasZonedSlots: items.some((i) => i.zonaId != null),
      forceFromStart,
      anchored,
    });
    return true;
  }

  /** Mesmos contentIds na mesma ordem — ignora campaignId (presigned refresh). */
  private isIdenticalActivePlaylist(items: PlaylistItem[]): boolean {
    if (!this.streamStore || items.length === 0) return false;
    const current = this.streamStore.playlist;
    if (current.length !== items.length) return false;
    return items.every((it, i) => it.id === current[i]?.id);
  }

  /**
   * Evita setPlaylist mid-playback quando o item atual ainda é válido.
   * Tab A7: sync com slot_sequence vazia caía em scheduler_backup ~4s após canPlay.
   */
  private shouldDeferPlaylistReapply(items: PlaylistItem[]): boolean {
    if (!this.streamStore || this.resetPlaylistFromStart) return false;
    if (this.isIdenticalActivePlaylist(items)) return true;
    const media = this.streamStore.currentMedia;
    if (!media || !this.streamStore.isPlaying) return false;
    return items.some((item) => item.id === media.id);
  }

  /**
   * REENTRADA PELO RELÓGIO — recoloca o player na posição canônica do grupo usando o que ele já
   * tem (época + relógio do servidor + sequência atual), sem rede.
   *
   * <p>É a implementação ÚNICA desse movimento. Dois chamadores:</p>
   * <ul>
   *   <li>o impulso {@code sync_correction} do supervisor (player saiu de fase);</li>
   *   <li>o fim de um gatilho em grupo exclusivo ({@code TriggerStore}): enquanto o player
   *   tocava o gatilho o relógio do grupo continuou andando, então "voltar onde parou" seria
   *   voltar atrasado — ele reentra onde o grupo ESTÁ.</li>
   * </ul>
   *
   * <p>Deliberadamente não dispara sync: a época e a sequência já estão em dia (o
   * {@code setFromSync} continua rodando durante o gatilho). Um sync completo aqui reabriria a
   * race do retorno de gatilho corrigida em 2026-05-28.</p>
   *
   * <p>Só faz sentido com época canônica; sem ela {@code resolveInitialIndex} devolve o índice
   * pedido (0) e o player recomeçaria do início — o chamador é quem garante o portão.</p>
   */
  realignToCanonicalClock(source: string): void {
    runInAction(() => {
      this.resetPlaylistFromStart = true;
    });
    this.applyStreamPlaylist(source);
  }

  /**
   * Centraliza a decisão slot_sequence vs scheduler (fallback/emergência R5).
   */
  private applyStreamPlaylist(
    source: string,
    campaignIdOverride?: string,
  ): void {
    if (!this.streamStore || !this.schedulerStore || triggerStore.isPlaying) {
      return;
    }

    const activePlaylist = this.schedulerStore.getActivePlaylist();
    const activeCampaign = this.schedulerStore.getActiveCampaign();
    const campaignId = campaignIdOverride ?? activeCampaign?.id;
    const forceFromStart = this.resetPlaylistFromStart;
    const playbackItems = playbackStore.isPlaylistMode
      ? playbackStore.toPlaylistItems()
      : [];
    const slotSequenceEmpty = playbackItems.length === 0;

    // R5 + orientação — scheduler manda quando:
    //   • sem campanha regular ativa (emergência/fallback), OU
    //   • slot_sequence vazia (retrato sem slots na playlist Tier 3).
    // Sem o 2º caso, slots landscape preservados/grudados bloqueavam emergência
    // e campanhas portrait mesmo com cache já atualizado (Renato Tab A7).
    if (
      playbackStore.isPlaylistMode &&
      activePlaylist.length > 0 &&
      (slotSequenceEmpty || !this.schedulerStore.hasActiveRegularCampaigns())
    ) {
      if (this.shouldDeferPlaylistReapply(activePlaylist)) {
        otaLog.info('applyStreamPlaylist: deferred (scheduler_fallback)', {
          source,
          mediaId: this.streamStore.currentMedia?.id?.slice(0, 8),
        });
        this.resetPlaylistFromStart = false;
        return;
      }
      this.streamStore.setPlaylist(
        activePlaylist,
        campaignId,
        false,
        forceFromStart ? 0 : this.streamStore.currentIndex,
        forceFromStart,
      );
      otaLog.info('Playlist source: scheduler_fallback', {
        source,
        items: activePlaylist.length,
      });
      this.resetPlaylistFromStart = false;
      return;
    }

    if (this.applyPlaylistFromPlaybackStore(forceFromStart)) {
      this.resetPlaylistFromStart = false;
      return;
    }

    // Server tier=3 com slots=[] (orientação) — sintetiza slots do scheduler.
    if (
      playbackStore.isPlaylistMode &&
      playbackStore.currentSlotSequence &&
      playbackStore.currentSlotSequence.slots.length === 0 &&
      activePlaylist.length > 0
    ) {
      playbackStore.hydrateSlotsFromPlaylistItems(activePlaylist);
      if (this.applyPlaylistFromPlaybackStore(forceFromStart)) {
        otaLog.info('Playlist source: slot_sequence (hydrated)', {
          source,
          items: activePlaylist.length,
          playlistId: playbackStore.currentSlotSequence?.playlistId,
        });
        this.resetPlaylistFromStart = false;
        return;
      }
    }

    if (!playbackStore.isPlaylistMode) {
      this.streamStore.setPlaylist(activePlaylist, campaignId);
      this.resetPlaylistFromStart = false;
      return;
    }

    if (activePlaylist.length > 0) {
      if (!forceFromStart && this.isIdenticalActivePlaylist(activePlaylist)) {
        otaLog.info('applyStreamPlaylist: skipped (identical items)', {
          source,
          items: activePlaylist.length,
        });
        this.resetPlaylistFromStart = false;
        return;
      }
      if (this.shouldDeferPlaylistReapply(activePlaylist)) {
        otaLog.info('applyStreamPlaylist: deferred (scheduler_backup)', {
          source,
          mediaId: this.streamStore.currentMedia?.id?.slice(0, 8),
        });
        this.resetPlaylistFromStart = false;
        return;
      }
      this.streamStore.setPlaylist(
        activePlaylist,
        campaignId,
        false,
        0,
        forceFromStart,
      );
      otaLog.info('Playlist source: scheduler_backup', {
        source,
        items: activePlaylist.length,
      });
      this.resetPlaylistFromStart = false;
      return;
    }

    console.warn(
      '[SyncStore] Tier 2/3 ativo mas slot_sequence vazia — mantendo playlist atual',
    );
    this.resetPlaylistFromStart = false;
  }

  /**
   * Initializes MediaStore to load cached metadata from previous sessions.
   * This ensures cache is available immediately on app startup.
   */
  private async initMediaStore(): Promise<void> {
    if (this.mediaStore) {
      console.log(
        '[SyncStore] Initializing MediaStore to load cached metadata...',
      );
      await this.mediaStore.init();

      // Update hasContent if we have cached items
      const cachedCount = this.mediaStore.cachedItems.length;
      if (cachedCount > 0) {
        console.log(
          `[SyncStore] Found ${cachedCount} cached items from previous session`,
        );
        runInAction(() => {
          this.hasContent = true;
        });
      }

      // 2026-06-12 (Renato): RACE CONDITION FIX. O `setupAuth reaction` que
      // dispara `initializeFromCache` pode rodar ANTES do `mediaStore.init()`
      // terminar (esta função é fire-and-forget na linha 494). Sem o
      // cacheStatus carregado, `usePlayableUrl` retorna null pro localUrl →
      // player tenta stream remoto → buffering eterno offline.
      //
      // Fix: depois do mediaStore.init() terminar, se as condições já estão
      // batendo (auth approved + scheduler tem cache + ainda sem playlist),
      // forçar o `initializeFromCache` agora — desta vez o cacheStatus está
      // populado, então `usePlayableUrl` resolve pro file:// local.
      //
      // 2026-09-23 (Renato + Claude): o gate de auth SAIU daqui. Ele exigia
      // `registrationStatus === 'approved'`, que no boot SEM REDE depende de uma
      // verificação com o servidor — ou seja, o único caminho offline pro conteúdo
      // estava condicionado a estar online. Medido no Tab A7 e no embedded: scheduler
      // restaurava 9 itens do disco, MediaStore confirmava 9 mídias cacheadas, e a tela
      // ia pra "Aguardando conteúdo" porque `initializeFromCache()` nunca era chamado.
      //
      // Sem o gate: o cache local é a fonte, e ele JÁ é filtrado — `getActivePlaylist()`
      // aplica as camadas AND (validade/janela/dia), então conteúdo vencido segue sem
      // tocar (R4 intacta). Device nunca aprovado não tem campanha em cache, então
      // `schedulerStore.hasContent` é false e nada acontece. E bloqueio administrativo
      // continua vencendo no Stream via `syncStore.isPlayerBlocked` assim que um sync
      // completa. Vide `.claude/rules/cache-offline-first.md` R3.
      if (
        this.schedulerStore?.hasContent &&
        (!this.streamStore?.playlist || this.streamStore.playlist.length === 0)
      ) {
        console.log(
          '[SyncStore] 🎯 Post-mediaStore-init: forçando cache-first agora com cacheStatus pronto',
        );
        this.initializeFromCache();
      }
    }
  }

  async sync(): Promise<void> {
    if (!this.syncService) {
      console.error('[SyncStore] Not initialized');
      return;
    }

    // CRITICAL: Don't try to sync if device is pending approval
    if (
      this.authStore &&
      this.authStore.registrationStatus === 'pending_approval'
    ) {
      console.log(
        '[SyncStore] ⚠️ Device is pending approval, skipping sync attempt',
        {
          registrationStatus: this.authStore.registrationStatus,
          deviceId: this.authStore.deviceId,
        },
      );
      return;
    }

    // CRITICAL: Don't try to sync if device has no credentials
    if (
      this.authStore &&
      (!this.authStore.deviceId || !this.authStore.playerId)
    ) {
      console.log('[SyncStore] Device not registered yet, skipping sync');
      return;
    }

    console.log(
      '[SyncStore] Action: sync() called. Status:',
      this.status,
      'Count:',
      this.syncCount,
    );

    runInAction(() => {
      this.status = 'syncing';
      this.error = null;
    });

    this.armSyncWatchdog();
    try {
      await this.syncService.sync();
    } finally {
      this.clearSyncWatchdog();
      this.syncStartedAt = null;
    }
  }

  /**
   * Forces a full re-sync by clearing the library hash.
   * Use this when orientation changes or when you need to guarantee
   * fresh data from server regardless of cache.
   *
   * Note: We don't check authStore.isAuthenticated here because:
   * 1. Polling bypasses this check and works fine with device ID auth
   * 2. If device isn't authenticated, server will return 401
   * 3. This allows orientation changes to trigger sync even when authStore state is stale
   */
  ensureSSEConnected(): void {
    this.syncService?.ensureSSEConnected();
  }

  /** Evita sync storm quando orientação flapping dispara forceResync em loop. */
  private lastForceResyncAt = 0;
  private static readonly FORCE_RESYNC_MIN_INTERVAL_MS = 20_000;

  async forceResync(options?: {
    resetPlaylistFromStart?: boolean;
  }): Promise<void> {
    if (!this.syncService) {
      console.error('[SyncStore] Not initialized');
      return;
    }

    const forceFromStart = options?.resetPlaylistFromStart === true;
    const now = Date.now();
    if (
      !forceFromStart &&
      this.lastForceResyncAt > 0 &&
      now - this.lastForceResyncAt < SyncStore.FORCE_RESYNC_MIN_INTERVAL_MS
    ) {
      otaLog.info('forceResync debounced', {
        sinceLastMs: now - this.lastForceResyncAt,
        minIntervalMs: SyncStore.FORCE_RESYNC_MIN_INTERVAL_MS,
      });
      return;
    }
    this.lastForceResyncAt = now;

    if (forceFromStart) {
      this.resetPlaylistFromStart = true;
    }

    console.log(
      '[SyncStore] Action: forceResync() called - clearing hash for orientation change',
    );

    runInAction(() => {
      this.status = 'syncing';
      this.error = null;
    });

    this.armSyncWatchdog();
    try {
      await this.syncService.forceResync();
    } finally {
      this.clearSyncWatchdog();
      this.syncStartedAt = null;
    }
  }

  /**
   * Starts automatic polling.
   */
  startPolling(intervalMs?: number): void {
    if (!this.syncService) {
      console.error('[SyncStore] Not initialized');
      return;
    }

    this.syncService.startPolling(intervalMs);
    console.log('[SyncStore] Polling started');

    // 2026-05-31 — boot do WallClockMonitor junto com o polling.
    // Cobre o midnight bug: força resync na virada de dia mesmo se
    // libraryHash não mudou. Vide doc no campo wallClockInterval.
    this.startWallClockMonitor();
  }

  /**
   * Stops automatic polling.
   */
  stopPolling(): void {
    if (!this.syncService) return;

    this.syncService.stopPolling();
    console.log('[SyncStore] Polling stopped');
  }

  // ===========================================================================
  // SYNC RESULT HANDLER
  // ===========================================================================

  private handleSyncResult = (result: SyncResult): void => {
    if (!result.success) {
      console.warn('[SyncStore] Sync failed:', result.error);
    }

    runInAction(() => {
      this.syncCount++;

      if (result.success) {
        // 2026-05-24: recovery instantâneo do debounce — reset do counter
        // de falhas. Próxima falha vai recomeçar do zero.
        this.consecutiveSyncFailures = 0;
        this.status = 'success';
        this.error = null;

        if (result.timestamp) {
          this.lastSyncTime = result.timestamp;
        }
        if (result.libraryHash) {
          this.libraryHash = result.libraryHash;
        }
        // Só atualiza se vier valor explícito (true ou false). Null/undefined
        // significa "server não soube determinar" — cliente preserva último
        // estado conhecido. CRÍTICO pra retail media com loja sem operador:
        // backend instável NÃO pode destravar o kiosk acidentalmente.
        if (
          result.kioskModeEnabled === true ||
          result.kioskModeEnabled === false
        ) {
          kioskStore.setServerEnabled(result.kioskModeEnabled);
        }

        // Sprint 2 CP8 — hidrata PlaybackStore com slot_sequence (Tier 2/3).
        // Idempotente: passar null/undefined em syncs de Tier 4 também é OK
        // (PlaybackStore reseta currentSlotSequence → null e tier → 4).
        // Passivo nesse CP — loop de execução (CP9) que vai consumir.
        // SYNC CANÔNICO NO TIER 2/3. Quem decide é o SERVER: ele carimba `playback_started_at`
        // nas campanhas do loop só quando garante que o grupo inteiro recebe a mesma composição
        // (grupo exclusivo — R11). Reusamos esse mesmo sinal em vez de inventar um segundo
        // conceito de "está sincronizado": se a época veio pros itens legados, ela vale pro loop
        // todo, e a slot_sequence (que É o loop) passa a se ancorar no relógio. Player sem o
        // carimbo segue idêntico ao de hoje — começa no slot 0 e caminha.
        const syncedItems = result.playlistItems ?? [];
        // A ÉPOCA em si, não um booleano: é ela que a slot_sequence precisa carimbar nos itens,
        // e é a MESMA que o caminho legado usa. Guardar só "ligado/desligado" foi o que deixou
        // a frota com duas épocas (vide `canonicalEpoch` na PlaybackStore).
        const canonicalEpoch =
          syncedItems.length > 0
            ? (syncedItems.find((item) => !!item.playbackStartedAt)
                ?.playbackStartedAt ?? null)
            : undefined;
        playbackStore.setFromSync(result.slotSequence ?? null, {
          preserveEmptySlots: !this.resetPlaylistFromStart,
          canonicalEpoch,
        });

        if (result.orientacaoId !== undefined) {
          this.orientacaoId = result.orientacaoId ?? null;
          this.orientationWidth = result.orientationWidth ?? null;
          this.orientationHeight = result.orientationHeight ?? null;
          this.screenOffsetX = result.screenOffsetX ?? 0;
          this.screenOffsetY = result.screenOffsetY ?? 0;
        }

        // Multi-zone layout — store zone definitions from sync response.
        // zones=undefined (server didn't send) → keep previous value.
        // zones=[] (no zones) → clear. zones=[...] → set new zones.
        if (result.zones !== undefined) {
          this.zones = result.zones ?? [];
        }

        // Persist orientation geometry + zones so an OFFLINE cold boot can
        // re-render multi-zone campaigns (reboot with no network).
        this.persistOrientationCache();

        // Available orientations for the player selector.
        if (result.availableOrientations !== undefined) {
          this.availableOrientations = result.availableOrientations ?? [];
          // Cache to localStorage for offline availability
          try {
            localStorage.setItem(
              'efflux_available_orientations',
              JSON.stringify(this.availableOrientations),
            );
          } catch {
            // localStorage unavailable — ignore
          }
        }

        // Reage à mudança de status do player.
        //   blocked → para playback + overlay e descarta o resto do result
        //   blocked → active/maintenance → cai no fluxo normal abaixo, que já
        //   processa o conteúdo que veio no MESMO payload (servidor devolve
        //   campaigns + playerStatus='active' juntos via SSE-triggered sync).
        if (result.playerStatus !== undefined) {
          const previousStatus = this.playerStatus;
          this.playerStatus = result.playerStatus;

          if (
            result.playerStatus === 'blocked' &&
            previousStatus !== 'blocked'
          ) {
            console.log(
              '[SyncStore] 🚫 Player blocked by admin — showing overlay',
            );
            // NÃO mexemos no streamStore aqui. O Stream scene já troca pra
            // overlay "Player bloqueado" via syncStore.isPlayerBlocked,
            // o que desmonta o VideoPlayer e pausa naturalmente. Limpar o
            // playlist/isPlaying causava re-mount com URL stale ao
            // desbloquear (player ficava preso em "Buffering..." preto).
            this.hasContent = false;
            this.stopRetryCountdown();
            return; // Skip o resto: não atualiza stores nem starta retry
          }

          if (
            previousStatus === 'blocked' &&
            (result.playerStatus === 'active' ||
              result.playerStatus === 'maintenance')
          ) {
            console.log(
              '[SyncStore] ✅ Player unblocked (status=' +
                result.playerStatus +
                ') — processing payload + requesting fresh sync',
            );
            // Player solicita conteúdo fresco proativamente. Chamamos direto
            // no SyncService pra bypassar o gate "Device not registered yet"
            // do sync.store.sync(). forceResync() limpa hash + lastSyncTime
            // e garante que o server devolva o response completo. Defensivo:
            // o payload atual já costuma vir com o conteúdo, mas se algum
            // edge case devolver vazio, esse segundo sync cobre.
            if (this.syncService) {
              this.syncService
                .forceResync()
                .catch((err) =>
                  console.warn(
                    '[SyncStore] Post-unblock forceResync failed:',
                    err,
                  ),
                );
            }
            // Cai no fluxo normal abaixo pra aplicar o conteúdo do payload
            // atual imediatamente (fast-path).
          }
        }

        // Check if we have content
        const receivedItems = !!(
          result.playlistItems && result.playlistItems.length > 0
        );
        const hasCampaigns = !!(
          result.campaigns && result.campaigns.length > 0
        );

        if (receivedItems || hasCampaigns) {
          console.log(
            `[SyncStore] ✅ Content: ${result.playlistItems?.length || 0} items, ${result.campaigns?.length || 0} campaigns`,
          );
          this.hasContent = true;
          this.consecutiveEmptySyncWhilePlaying = 0;
          // Stop any retry countdown since we have content
          this.stopRetryCountdown();
          this.updateStores(result);
        } else {
          // No NEW content received from server
          if (!this.hasContent) {
            if (result.campaigns !== undefined) {
              console.log(
                '[SyncStore] Empty library accepted — showing waiting-for-content screen',
              );
              this.consecutiveEmptySyncWhilePlaying = 0;
              this.stopRetryCountdown();
              this.updateStores(result);
              return;
            }

            // 2026-05-31 — FIX MIDNIGHT TRAVA (Renato observou em prod).
            //
            // <p><b>Cenário:</b> Cliente Tizen TV à meia-noite entra em estado
            // travado. {@code hasContent=false} (não recebeu conteúdo) mas
            // {@code libraryHash} local já tem valor (de sync anterior bem-
            // sucedido onde algum efeito colateral falhou silencioso —
            // memory pressure no Tizen WebView, MobX reaction não disparou,
            // etc.). Server sempre responde {@code update_required: no}
            // (hash bate) → handler vê {@code campaigns=undefined} → NÃO chama
            // setCampaigns → cliente fica preso pra sempre sem intervenção.</p>
            //
            // <p><b>Fix:</b> ao detectar essa inconsistência
            // ({@code !hasContent} + sync sucesso + server diz no-update),
            // limpa {@code libraryHash} via {@link forceResync} → próximo
            // sync recebe payload completo. Loop natural até resolver.</p>
            //
            // <p>Reload do app não resolve porque {@code libraryHash} persiste
            // (testado em campo). Único caminho seguro: server enviar dados
            // de novo, e pra isso o cliente precisa esquecer o hash.</p>
            if (this.libraryHash) {
              console.warn(
                '[SyncStore] 🚨 Estado inconsistente — hasContent=false mas libraryHash existe (server diz no-update). Limpando hash + forceResync pra recovery.',
              );
              this.startRetryCountdown();
              if (this.syncService) {
                this.syncService
                  .forceResync()
                  .catch((err) =>
                    console.warn(
                      '[SyncStore] Inconsistent state recovery falhou:',
                      err,
                    ),
                  );
              }
              return;
            }
            console.log(
              '[SyncStore] ⚠️ No content received from server (empty playlist)',
            );
            this.startRetryCountdown();
          } else {
            const isExplicitEmptyLibrary =
              result.campaigns !== undefined &&
              result.campaigns.length === 0 &&
              (!result.playlistItems || result.playlistItems.length === 0);
            const hadLocalCampaigns =
              (this.schedulerStore?.campaigns.length ?? 0) > 0;

            if (
              isExplicitEmptyLibrary &&
              hadLocalCampaigns &&
              Date.now() < this.midnightRecoveryUntil
            ) {
              console.warn(
                '[SyncStore] ⚠️ Midnight window — rejeitando biblioteca vazia do server (mantém retry + forceResync)',
              );
              this.startRetryCountdown();
              if (this.syncService) {
                this.syncService
                  .forceResync()
                  .catch((err) =>
                    console.warn(
                      '[SyncStore] Midnight empty-library forceResync falhou:',
                      err,
                    ),
                  );
              }
              return;
            }

            if (isExplicitEmptyLibrary) {
              console.log(
                '[SyncStore] Empty library accepted — clearing local campaigns and showing waiting-for-content screen',
              );
              this.consecutiveEmptySyncWhilePlaying = 0;
              this.stopRetryCountdown();
              this.updateStores(result);
              return;
            }

            // Server said "no update required" - keep existing content
            // CRITICAL: Only call updateStores if campaigns is explicitly defined (not undefined)
            // undefined = server sent no data (update_required: no), keep existing
            // [] = server explicitly sent empty array (campaign was removed)
            if (result.campaigns !== undefined) {
              console.log(
                '[SyncStore] 🔄 Updating stores with',
                result.campaigns.length,
                'campaigns (explicit update)',
              );
              this.updateStores(result);
            } else {
              console.log(
                '[SyncStore] ℹ️ No update required - keeping existing content (campaigns undefined)',
              );
              // Orientação física / hash short-circuit: server manda slot_sequence
              // nova (conteúdo retrato) mas campaigns=undefined — setFromSync já
              // rodou; sem applyStreamPlaylist o Stream fica na playlist antiga.
              this.applyPlaylistAfterNoUpdateSync(result);
              if (this.schedulerStore!.getActivePlaylist().length === 0) {
                this.reconcileEmptyPlaybackState('no-update-sync');
              }
            }
          }
        }
      } else {
        // Check for authentication error
        if (result.authError) {
          console.warn('[SyncStore] Authentication error detected', {
            registrationStatus: this.authStore?.registrationStatus,
            isAuthenticated: this.authStore?.isAuthenticated,
          });

          // CRITICAL: If device is pending approval, don't try to refresh session
          // Just log the error and wait - the device will be approved via SSE/polling
          if (
            this.authStore &&
            this.authStore.registrationStatus === 'pending_approval'
          ) {
            console.log(
              '[SyncStore] Device is pending approval, ignoring auth error - will wait for approval',
            );
            runInAction(() => {
              this.status = 'error';
              this.error =
                'Device pending approval - waiting for admin approval';
            });
            return;
          }

          // Check if we can retry authentication
          if (this.authStore && !this.isRetryingAuth) {
            console.log('[SyncStore] Attempting to refresh session...');
            this.isRetryingAuth = true;

            // Try to refresh session
            this.authStore.refreshSession().then((success) => {
              runInAction(() => {
                this.isRetryingAuth = false;
                if (success) {
                  console.log(
                    '[SyncStore] Session refreshed. Sync will be triggered by reaction.',
                  );
                  // We don't call this.sync() here because the reaction to authStore.isAuthenticated
                  // will already trigger it. Calling it here would cause double-sync.
                } else {
                  console.error(
                    '[SyncStore] Failed to refresh session, logging out',
                  );
                  this.handleAuthFailure(result.error);
                }
              });
            });
            return;
          } else if (this.isRetryingAuth) {
            console.log(
              '[SyncStore] Auth retry already in progress, waiting...',
            );
            return;
          }

          // If we are here, retrying failed or is not possible
          this.handleAuthFailure(result.error);
          return;
        }

        // 2026-05-24: debounce de falhas consecutivas. 1 falha (cold-start
        // do Railway, jitter de rede móvel, throttle de Wi-Fi de loja) NÃO
        // deve mudar a UI pra "Sem acesso ao servidor Efflux" imediatamente.
        // Promove status='error' apenas após N falhas seguidas. Antes do
        // threshold, mantemos o último status conhecido (provavelmente
        // 'syncing' ou 'success') — a UI continua exibindo a tela anterior
        // ("Sincronizando com servidor..." ou cache em playback).
        this.consecutiveSyncFailures++;
        const debounceThreshold = this.getSyncFailureDebounceThreshold();
        const shouldPromoteToError =
          this.consecutiveSyncFailures >= debounceThreshold;

        console.warn(
          `[SyncStore] Sync failure ${this.consecutiveSyncFailures}/${debounceThreshold} — ${shouldPromoteToError ? 'PROMOTING to status=error' : 'staying in debounce (UI unchanged)'}`,
        );

        if (shouldPromoteToError) {
          this.status = 'error';
          this.error = result.error || 'Unknown error';
        }
        // Do NOT clear hasContent on error - keep playing cached content
        // this.hasContent = false;

        // Try to play from cache if available — independente de debounce,
        // se temos cache disponível vale a pena inicializar (UX melhor mesmo
        // antes do estado de erro promovido).
        if (this.schedulerStore?.hasContent && !this.hasContent) {
          console.log(
            '[SyncStore] 🔄 Sync failed but cache available, initializing from cache',
          );
          this.initializeFromCache();
        }

        // Only start retry countdown if we still don't have content after trying cache.
        // Importante: o countdown só é iniciado quando promovemos pra 'error' —
        // antes disso, ainda estamos em estado de "syncing" do ponto de vista da UI,
        // e o próximo sync agendado pelo SyncService (polling/SSE) já vai disparar.
        if (!this.hasContent && shouldPromoteToError) {
          this.startRetryCountdown();
        }
      }
    });
  };

  /**
   * 2026-05-24: lê o threshold de debounce de falhas de sync.
   * Default DEFAULT_SYNC_FAILURE_DEBOUNCE (=2), override via env var
   * VITE_SYNC_FAILURE_DEBOUNCE pra ajustar em ambientes flaky.
   * Validação defensiva: valores inválidos / ≤0 caem no default.
   */
  private getSyncFailureDebounceThreshold(): number {
    try {
      const raw = import.meta.env.VITE_SYNC_FAILURE_DEBOUNCE;
      if (raw === undefined || raw === '') return DEFAULT_SYNC_FAILURE_DEBOUNCE;
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) return DEFAULT_SYNC_FAILURE_DEBOUNCE;
      return Math.floor(n);
    } catch {
      return DEFAULT_SYNC_FAILURE_DEBOUNCE;
    }
  }

  /**
   * Initializes playback from cached campaigns when sync fails but cache exists.
   * Called when network error occurs but we have cached content to play.
   */
  private initializeFromCache(): void {
    if (!this.schedulerStore || !this.streamStore) return;

    // Check if scheduler has cached campaigns
    if (!this.schedulerStore.hasContent) {
      console.log('[SyncStore] No cached campaigns available');
      return;
    }

    // Start evaluation if not already running
    this.schedulerStore.startEvaluation();

    // Get active playlist from cached campaigns
    const activePlaylist = this.schedulerStore.getActivePlaylist();

    if (activePlaylist.length > 0) {
      // Don't overwrite trigger playlist if a trigger is actively playing
      if (triggerStore.isPlaying) {
        console.log(
          '[SyncStore] ⚠️ Trigger active — skipping cache setPlaylist to preserve trigger playback',
        );
      } else {
        this.applyStreamPlaylist('cache-init');
      }
      runInAction(() => {
        this.hasContent = true;
      });
    } else {
      console.log(
        '[SyncStore] ⚠️ Cached campaigns exist but no active playlist',
      );
    }
  }

  private handleAuthFailure(errorMsg?: string): void {
    this.status = 'auth_error';
    this.hasContent = false;
    // Stop polling and countdown on auth error
    this.stopPolling();
    this.stopRetryCountdown();

    // Logout to redirect to auth screen
    // Device may have been blocked, rejected, or not found
    this.error = errorMsg || 'Device not authenticated';
    console.error('[SyncStore] Auth failure:', this.error);

    // 🆕 CRITICAL FIX: If device is not authenticated, try automatic registration
    if (this.authStore && !this.authStore.isAuthenticated) {
      const registrationStatus = this.authStore.registrationStatus;

      console.log(
        '[SyncStore] 🔄 Device not authenticated - checking registration status:',
        registrationStatus,
      );

      // If device has never been registered or was rejected, initiate automatic registration
      if (
        registrationStatus === 'idle' ||
        registrationStatus === 'error' ||
        registrationStatus === 'rejected'
      ) {
        console.log(
          '[SyncStore] 🚀 Initiating automatic device registration...',
        );

        // Import platform service and trigger registration
        import('~/services/platform').then(() => {
          try {
            // Get platform instance from context (we need to use a hook workaround)
            // Since we're in a store, we'll use the existing platform reference if available
            const platformService = window.__effluxPlatform;

            if (platformService && this.authStore) {
              console.log(
                '[SyncStore] ✅ Platform service found, calling initializeWithPlatform',
              );
              this.authStore.initializeWithPlatform(platformService);
            } else {
              console.warn(
                '[SyncStore] ⚠️ Platform service not found on window.__effluxPlatform',
              );
              // Fallback: logout to redirect to auth screen
              if (this.authStore) {
                this.authStore.logout();
              }
            }
          } catch (error) {
            console.error(
              '[SyncStore] ❌ Error during automatic registration:',
              error,
            );
            // Fallback: logout
            if (this.authStore) {
              this.authStore.logout();
            }
          }
        });

        return; // Don't logout immediately, wait for registration to complete
      }

      // If device is already pending approval, don't logout - just wait
      if (registrationStatus === 'pending_approval') {
        console.log(
          '[SyncStore] ⏳ Device is pending approval - waiting for admin approval',
        );
        return; // Don't logout, let the polling/SSE handle approval
      }
    }

    // If we reach here, logout to redirect to auth screen
    if (this.authStore) {
      this.authStore.logout();
    }
  }

  /**
   * Sync com update_required=no ainda pode trazer slot_sequence regenerada
   * (ex.: rotação física landscape→portrait). playbackStore já foi hidratado
   * em handleSyncResult; falta empurrar pro Stream.
   */
  private applyPlaylistAfterNoUpdateSync(result: SyncResult): void {
    if (!this.streamStore || !this.schedulerStore || triggerStore.isPlaying) {
      return;
    }

    const hasSlotSequence = result.slotSequence != null;
    if (!hasSlotSequence && !this.resetPlaylistFromStart) {
      return;
    }

    if (!playbackStore.isPlaylistMode && !this.resetPlaylistFromStart) {
      return;
    }

    if (result.playerGroups !== undefined) {
      this.schedulerStore.setPlayerGroups(result.playerGroups);
    }
    if (result.playerSchedule !== undefined) {
      this.schedulerStore.setPlayerSchedule(result.playerSchedule);
    }
    if (result.playlistSchedules !== undefined) {
      this.schedulerStore.setPlaylistSchedules(result.playlistSchedules);
    }

    otaLog.info('applyPlaylistAfterNoUpdateSync: slot_sequence no-update', {
      hasSlotSequence,
      resetFromStart: this.resetPlaylistFromStart,
      tier: playbackStore.currentTier,
      slots: playbackStore.currentSlotSequence?.slots.length ?? 0,
    });
    this.applyStreamPlaylist('no-update-slot-sequence');
  }

  private updateStores(result: SyncResult): void {
    console.log('[SyncStore] 📦 updateStores called with:', {
      campaignsCount: result.campaigns?.length || 0,
      playlistItemsCount: result.playlistItems?.length || 0,
      hasSchedulerStore: !!this.schedulerStore,
      hasStreamStore: !!this.streamStore,
      hasMediaStore: !!this.mediaStore,
      campaigns: result.campaigns?.map((c) => ({
        id: c.id,
        name: c.name,
        playlistLength: c.playlist?.length || 0,
        status: c.status,
      })),
    });

    // V060/V066/V067 — propaga janelas mesmo em no-update (result.campaigns
    // undefined). Schedule do grupo/player/playlist pode mudar sem mexer em
    // library_hash; sem isto, cliente ficaria estagnado até alguma
    // campanha mudar. setPlayerGroups/setPlayerSchedule/setPlaylistSchedules
    // não disparam evaluateSchedule sozinhos — só atualizam o estado pro
    // próximo tick.
    if (this.schedulerStore) {
      if (result.playerGroups !== undefined) {
        this.schedulerStore.setPlayerGroups(result.playerGroups);
      }
      if (result.playerSchedule !== undefined) {
        this.schedulerStore.setPlayerSchedule(result.playerSchedule);
      }
      if (result.playlistSchedules !== undefined) {
        this.schedulerStore.setPlaylistSchedules(result.playlistSchedules);
      }
    }

    // Update scheduler with new campaigns
    if (this.schedulerStore && result.campaigns !== undefined) {
      this.schedulerStore.setCampaigns(result.campaigns);
      this.schedulerStore.startEvaluation();
    }

    // Update TriggerStore with trigger definitions from sync
    if (result.triggers && result.triggers.length > 0) {
      console.log(
        `[SyncStore] 🎯 Passing ${result.triggers.length} trigger definitions to TriggerStore`,
      );
      triggerStore.setTriggerDefinitions(result.triggers);
      // Connect triggerStore to streamStore if not already done
      if (this.streamStore) {
        triggerStore.setStreamStore(this.streamStore);
      }
    }

    // Update stream with active playlist (always — empty list reverts to fallback/no-content UI)
    if (this.streamStore && this.schedulerStore) {
      const activePlaylist = this.schedulerStore.getActivePlaylist();

      if (triggerStore.isPlaying) {
        console.log(
          '[SyncStore] ⚠️ Trigger active — skipping sync setPlaylist',
        );
      } else {
        this.applyStreamPlaylist('sync-result');
      }

      // v1.0.42 — NoContent recovery automático.
      // v1.0.91 (2026-05-31) — One-shot `setTimeout(30s)` virou recurring
      // `startRetryCountdown()` (30s loop indefinido).
      //
      // Caso real (task #54): às 00:00 em ponto a campanha A expira (endDate
      // = ontem) mas o servidor ainda envia A junto com B (nova, startDate=
      // hoje). getActivePlaylist() retorna [] porque A já passou e B pode
      // ter startTime > 00:00 (ex: 06:00). Player fica em "Aguardando
      // conteúdo".
      //
      // Antes: agendava forceResync UMA vez em 30s. Se ainda viesse vazio
      // (B ainda inativa), próxima tentativa era o polling natural (5-30min).
      // Renato observou v1.0.90: TV com 2 campanhas + 10 conteúdos parou
      // de tocar à meia-noite e ficava 4min sem retry porque hasContent
      // virava true (server mandava itens) → stopRetryCountdown() →
      // polling 5min. Tarde demais.
      //
      // Defesa nova: dispara startRetryCountdown() — loop 30s contando até
      // a sync trazer item válido (hasContent permanece true mas a playlist
      // ativa pode mudar a cada sync se filtro horário muda).
      const hasCampaignsButNoPlaylist =
        result.campaigns &&
        result.campaigns.length > 0 &&
        activePlaylist.length === 0;
      if (hasCampaignsButNoPlaylist && this.syncService) {
        console.warn(
          '[SyncStore] ⚠️ Sync trouxe campaigns mas activePlaylist=[] (provavelmente transição de janela horária). Iniciando retry a cada 30s.',
        );
        this.startRetryCountdown();
      }
    }

    // Register interactive elements by contentId for lookup during playback
    if (result.playlistItems) {
      const entries = result.playlistItems
        .filter((item) => item.elementos && item.elementos.length > 0)
        .map((item) => ({ contentId: item.id, elementos: item.elementos! }));
      interactiveElementsStore.registerAllElements(entries);
    }

    // Sync cache and prefetch in background (non-blocking)
    if (this.mediaStore && result.playlistItems) {
      this.syncCacheInBackground(result.playlistItems);
      // 2026-06-25 — Quando o server faz hash short-circuit (library
      // inalterada), playlistItems vem como [] → prefetchAll early-return
      // → items errored ficam sem retry e toda exibição cai pro streaming
      // HTTPS. Disparar retry-sweep aqui garante recovery de transientes
      // (download truncado, checksum/size mismatch) mesmo sem mudança na
      // library. Idempotente: queueDownload tem gates de cooldown,
      // isInQueue, status='downloading'/'saving'.
      if (result.playlistItems.length === 0) {
        this.mediaStore.retryFailedDownloads();
      }
    }
  }

  /**
   * Syncs cache with schedule and prefetches content in background.
   * This runs asynchronously to not block UI updates.
   */
  private async syncCacheInBackground(
    playlistItems: import('~/services/scheduler/types').PlaylistItem[],
  ): Promise<void> {
    try {
      // Inclui a mídia dos overlays (MEDIA_OVERLAY) no conjunto a cachear —
      // assim a moldura/sobreposição fica offline + aparece instantânea, em
      // vez de baixar on-demand a cada exibição. Player-only, sem mudança de
      // contrato (a mídia já vem presigned no sync). Vide buildOverlayCacheItems.
      const overlayItems = buildOverlayCacheItems(playlistItems);
      const allItems =
        overlayItems.length > 0
          ? [...playlistItems, ...overlayItems]
          : playlistItems;

      // First: Remove orphaned assets (not in current schedule)
      await this.mediaStore!.syncWithSchedule(allItems);

      // Second: Prefetch all scheduled content for offline resilience
      const currentMediaId = this.streamStore?.currentMedia?.id;
      await this.mediaStore!.prefetchAll(allItems, currentMediaId);
    } catch (error) {
      console.error('[SyncStore] Error syncing cache in background:', error);
    }
  }

  // ===========================================================================
  // DEBUG
  // ===========================================================================

  debugState(): void {
    console.group('[SyncStore] Debug State');
    console.log('Status:', this.status);
    console.log('Is online:', this.isOnline);
    console.log('Last sync:', this.lastSyncTimeFormatted);
    console.log('Library hash:', this.libraryHash?.substring(0, 16) + '...');
    console.log('Sync count:', this.syncCount);
    console.log('Error:', this.error);
    console.groupEnd();
  }

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  reset(): void {
    this.stopPolling();
    this.stopRetryCountdown();
    this.stopWallClockMonitor();

    if (this.configListener) {
      this.configListener();
      this.configListener = null;
    }

    if (this.syncService) {
      this.syncService.destroy();
      this.syncService = null;
    }

    if (this.connectionMonitor) {
      this.connectionMonitor.stop();
      this.connectionMonitor = null;
    }

    runInAction(() => {
      this.status = 'idle';
      this.lastSyncTime = null;
      this.libraryHash = null;
      this.isOnline = true;
      this.isInternetReachable = true;
      this.isServerReachable = true;
      this.connectionState = 'online';
      this.error = null;
      this.syncCount = 0;
      this.hasContent = false;
      this.retryCountdown = 0;
      this.retryTargetAt = null;
      this.isRetryingAuth = false;
      this.midnightRecoveryUntil = 0;
      this.zones = [];
      this.orientacaoId = null;
      this.orientationWidth = null;
      this.orientationHeight = null;
      this.screenOffsetX = 0;
      this.screenOffsetY = 0;
      // Note: availableOrientations intentionally NOT cleared on reset.
      // They persist across resets so the selector remains available
      // between syncs. Cleared only on auth change (new device).
    });

    // Drop the cached orientation/zone layout so a re-provisioned device does
    // not restore a previous tenant's zones on the next boot.
    try {
      localStorage.removeItem('efflux_cached_orientation');
    } catch {
      // localStorage unavailable — ignore
    }

    this.authStore = null;
  }

  onConfigUpdate(callback: ConfigUpdateCallback): (() => void) | undefined {
    if (!this.syncService) {
      // Race benigna — ConfigManager faz retry sozinho via trySubscribe.
      console.debug(
        '[SyncStore] onConfigUpdate called but syncService is not ready',
      );
      return undefined;
    }

    const wrapped = (config: {
      playerId?: string;
      volumeLevel?: number;
      resetVolumeTimeoutSeconds?: number;
      batteryWarningEnabled?: boolean;
      audioAlertEnabled?: boolean;
      audioAlertVolumeLevel?: number;
      audioAlertBeepCount?: number;
      audioAlertIntervalMinutes?: number;
      audioAlertLowBatteryBeepCount?: number;
      audioAlertLowBatteryIntervalMinutes?: number;
      visualAlertEnabled?: boolean;
      visualAlertBlinkCount?: number;
      visualAlertDurationSeconds?: number;
      visualAlertLowBatteryBlinkCount?: number;
      visualAlertLowBatteryDurationSeconds?: number;
      backgroundPortraitUrl?: string;
      backgroundLandscapeUrl?: string;
      kioskModeEnabled?: boolean;
      kioskUnlockPinHashes?: string[];
      // V046 — Brilho
      brightnessAuto?: boolean;
      brightnessLevel?: number;
    }) => {
      console.log('[SyncStore] Config update received:', config);
      runInAction(() => {
        this.lastConfigUpdate = config;
      });
      callback(config);
    };

    return this.syncService.onConfigUpdate(wrapped);
  }

  getLastConfigUpdate(): {
    volumeLevel?: number;
    resetVolumeTimeoutSeconds?: number;
    batteryWarningEnabled?: boolean;
    audioAlertEnabled?: boolean;
    audioAlertVolumeLevel?: number;
    audioAlertBeepCount?: number;
    audioAlertIntervalMinutes?: number;
    audioAlertLowBatteryBeepCount?: number;
    audioAlertLowBatteryIntervalMinutes?: number;
    visualAlertEnabled?: boolean;
    visualAlertBlinkCount?: number;
    visualAlertDurationSeconds?: number;
    visualAlertLowBatteryBlinkCount?: number;
    visualAlertLowBatteryDurationSeconds?: number;
    backgroundPortraitUrl?: string;
    backgroundLandscapeUrl?: string;
    kioskModeEnabled?: boolean;
    kioskUnlockPinHashes?: string[];
    // V046 — Brilho
    brightnessAuto?: boolean;
    brightnessLevel?: number;
    // Cadência do relatório de playback (vide a declaração privada do campo).
    playbackReportIntervalSeconds?: number;
  } | null {
    return this.lastConfigUpdate;
  }

  async ackConfig(playerId: string): Promise<void> {
    if (this.syncService) {
      await this.syncService.ackConfig(playerId);
    }
  }

  /**
   * Fetches the latest configuration from the server and updates the store state.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- proxy direto de SyncService.fetchConfig() (mesmo motivo: config shape varia por feature flag do server)
  async fetchConfig(): Promise<any> {
    if (this.syncService) {
      try {
        const config = await this.syncService.fetchConfig();
        if (config) {
          runInAction(() => {
            this.lastConfigUpdate = config;
            // Só atualiza se vier valor explícito true/false.
            if (
              config.kioskModeEnabled === true ||
              config.kioskModeEnabled === false
            ) {
              kioskStore.setServerEnabled(config.kioskModeEnabled);
            }
            if (config.kioskUnlockPinHashes !== undefined) {
              kioskStore.setUnlockPinHashes(config.kioskUnlockPinHashes);
            }
          });
          // Drena fila offline de auditoria após fetch inicial bem-sucedido
          this.drainKioskAuditQueue().catch((err) =>
            console.warn(
              '[SyncStore] Drain audit queue after fetch failed:',
              err,
            ),
          );
        }
        return config;
      } catch (error) {
        console.error('[SyncStore] Error fetching config:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Update the device ID used by the sync service.
   */
  updateDeviceId(deviceId: string): void {
    if (this.syncService) {
      this.syncService.updateDeviceId(deviceId);
    }
  }

  /**
   * Define as tags do HOST embedded (Fase 4a/§16). Delega ao SyncService, que
   * normaliza/persiste e dispara um sync imediato quando o set muda. Chamado
   * SÓ pelo canal embedded (`start(config).tags`) — no-op se o sync ainda não
   * inicializou (o SyncService relê do localStorage ao subir).
   */
  setHostTags(tags: string[] | null | undefined): void {
    if (this.syncService) {
      this.syncService.setHostTags(tags);
    }
  }

  /**
   * Drena a fila offline de eventos de auditoria do kiosk pra o servidor.
   * Chamado após reconexão / config update / sync com sucesso.
   *
   * Cada evento que retornar 2xx é removido da fila local; falhas ficam
   * pra próxima tentativa.
   */
  async drainKioskAuditQueue(): Promise<void> {
    if (!this.syncService) return;

    const playerId = this.lastConfigUpdate?.playerId;
    if (!playerId) {
      // Sem playerId resolvido ainda, não dá pra reportar
      return;
    }

    const events = kioskStore.getPendingAuditEvents();
    if (events.length === 0) return;

    const sentIds: string[] = [];
    for (const event of events) {
      const ok = await this.syncService.reportKioskUnlockAttempt(playerId, {
        success: event.success,
        lockoutTriggered: event.lockoutTriggered,
        lockoutDurationSec: event.lockoutDurationSec,
        clientEventId: event.clientEventId,
      });
      if (ok) {
        sentIds.push(event.clientEventId);
      } else {
        // Falha intermitente — para o loop pra evitar burst de erros.
        // Os events restantes vão na próxima tentativa.
        break;
      }
    }

    if (sentIds.length > 0) {
      kioskStore.markAuditEventsSent(sentIds);
      console.log(
        `[SyncStore] Drenou ${sentIds.length} evento(s) kiosk audit pendentes`,
      );
    }
  }
}

export default SyncStore;
